import { useLayoutEffect, useRef, useState } from 'react';

// How far past a tab's edge the strip scrolls to reveal it: room for the active tab's curved shoulder (40px)
// to clear the fade.
const REVEAL_MARGIN = 40;

export type ScrollDirection = 'start' | 'end';

type Clipping = { isClippedAtStart: boolean; isClippedAtEnd: boolean; isScrolledFromStart: boolean };

// A pixel of slack, since scrollLeft can be fractional at the end. The edges are read at `scrollLeft`, where a
// smooth scroll is headed; `isScrolledFromStart` is where the strip is now.
function measureClipping(viewport: HTMLElement, scrollLeft: number): Clipping {
  return {
    isClippedAtStart: scrollLeft > 1,
    isClippedAtEnd: scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1,
    isScrolledFromStart: viewport.scrollLeft > 1,
  };
}

// Within the margin of either end, go all the way, so a sliver of strip doesn't keep a fade and an arrow.
function snapToEnds(viewport: HTMLElement, scrollLeft: number) {
  const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;

  if (scrollLeft < REVEAL_MARGIN) return 0;
  if (scrollLeft > maxScrollLeft - REVEAL_MARGIN) return maxScrollLeft;

  return scrollLeft;
}

/**
 * Sideways scrolling for a tab strip wider than its container. Attach `viewportRef` to the scroll container
 * around the tabs. It reports which edges are clipped, keeps the active or focused tab in view, and
 * `scrollToward` steps a tab-aligned page toward one edge.
 */
export default function useTabStripScroll() {
  const viewportRef = useRef<HTMLDivElement>(null);
  // Where a `scrollToward` smooth scroll is headed. Clipping is read from it rather than from the position
  // mid-scroll, so an arrow whose edge is about to run out hides on the click, not once the strip arrives.
  const scrollTargetRef = useRef<number | null>(null);

  // Separate booleans rather than one object: setting a flag to the value it already has, as most scroll events
  // do, skips the re-render.
  const [isClippedAtStart, setIsClippedAtStart] = useState(false);
  const [isClippedAtEnd, setIsClippedAtEnd] = useState(false);
  const [isScrolledFromStart, setIsScrolledFromStart] = useState(false);

  function setClipping(clipping: Clipping) {
    setIsClippedAtStart(clipping.isClippedAtStart);
    setIsClippedAtEnd(clipping.isClippedAtEnd);
    setIsScrolledFromStart(clipping.isScrolledFromStart);
  }

  // A layout effect, so the strip is first painted already scrolled to the active tab, with its fades.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    function updateClipping() {
      if (!viewport) return;

      const scrollTarget = scrollTargetRef.current;
      if (scrollTarget !== null && Math.abs(viewport.scrollLeft - scrollTarget) < 1) scrollTargetRef.current = null;

      setClipping(measureClipping(viewport, scrollTargetRef.current ?? viewport.scrollLeft));
    }

    // The user took over (or the browser gave up), so clipping follows the real position again.
    function dropScrollTarget() {
      scrollTargetRef.current = null;
      updateClipping();
    }

    function revealTab(tab: Element | null) {
      if (!viewport || !tab) return;

      const tabRect = tab.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      let scrollLeft = viewport.scrollLeft;

      if (tabRect.left < viewportRect.left + REVEAL_MARGIN) {
        scrollLeft += tabRect.left - viewportRect.left - REVEAL_MARGIN;
      } else if (tabRect.right > viewportRect.right - REVEAL_MARGIN) {
        scrollLeft += tabRect.right - viewportRect.right + REVEAL_MARGIN;
      } else {
        return;
      }

      scrollTargetRef.current = null;
      // scrollLeft rather than scrollIntoView, which would also scroll the page or drawer around the tabs.
      viewport.scrollLeft = snapToEnds(viewport, scrollLeft);
    }

    function revealActiveTab() {
      revealTab(viewport?.querySelector('[role="tab"][data-state="active"]') ?? null);
    }

    function handleFocusIn(event: FocusEvent) {
      if (event.target instanceof Element) revealTab(event.target);
    }

    revealActiveTab();
    updateClipping();

    viewport.addEventListener('scroll', updateClipping, { passive: true });
    viewport.addEventListener('scrollend', dropScrollTarget);
    // Safari has no scrollend: a wheel or a touch also hands the scrolling back to the user.
    viewport.addEventListener('wheel', dropScrollTarget, { passive: true });
    viewport.addEventListener('touchstart', dropScrollTarget, { passive: true });
    viewport.addEventListener('focusin', handleFocusIn);

    // A resize, such as the font loading after mount, can push the active tab back out of view.
    const resizeObserver = new ResizeObserver(() => {
      revealActiveTab();
      updateClipping();
    });

    resizeObserver.observe(viewport);
    if (viewport.firstElementChild) resizeObserver.observe(viewport.firstElementChild);

    // Radix flags the active tab with data-state, whether the value is controlled or not.
    const mutationObserver = new MutationObserver(revealActiveTab);
    mutationObserver.observe(viewport, { subtree: true, attributeFilter: ['data-state'] });

    return () => {
      viewport.removeEventListener('scroll', updateClipping);
      viewport.removeEventListener('scrollend', dropScrollTarget);
      viewport.removeEventListener('wheel', dropScrollTarget);
      viewport.removeEventListener('touchstart', dropScrollTarget);
      viewport.removeEventListener('focusin', handleFocusIn);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  // About a page at a time, lined up on a tab: the tab cut off at that edge moves to the opposite edge, fully in
  // view, so no tab is ever skipped half-seen.
  function scrollToward(direction: ScrollDirection) {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const viewportRect = viewport.getBoundingClientRect();
    const tabRects = Array.from(viewport.querySelectorAll('[role="tab"]'), (tab) => tab.getBoundingClientRect());
    let scrollLeft = scrollTargetRef.current ?? viewport.scrollLeft;
    // Measured against where the strip is now; an unfinished scroll adds the distance still to go.
    const distanceToGo = scrollLeft - viewport.scrollLeft;

    if (direction === 'end') {
      const cutOffTab = tabRects.find(({ right }) => right - distanceToGo > viewportRect.right - REVEAL_MARGIN);
      if (!cutOffTab) return;

      scrollLeft += cutOffTab.left - distanceToGo - viewportRect.left - REVEAL_MARGIN;
    } else {
      const cutOffTab = tabRects.findLast(({ left }) => left - distanceToGo < viewportRect.left + REVEAL_MARGIN);
      if (!cutOffTab) return;

      scrollLeft += cutOffTab.right - distanceToGo - viewportRect.right + REVEAL_MARGIN;
    }

    const scrollTarget = snapToEnds(viewport, scrollLeft);
    if (Math.abs(scrollTarget - viewport.scrollLeft) < 1) return;

    scrollTargetRef.current = scrollTarget;
    setClipping(measureClipping(viewport, scrollTarget));
    viewport.scrollTo({ left: scrollTarget, behavior: 'smooth' });
  }

  return { viewportRef, isClippedAtStart, isClippedAtEnd, isScrolledFromStart, scrollToward };
}
